import { useMutation, useQuery } from "@apollo/client";
import { Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AutoSizer, { Size } from "react-virtualized-auto-sizer";
import { useAppSelector } from "../../app/hooks";
import useDocumentTitle, { stoneName } from "../widgets/useDocumentTitle";
import {
  ANALYZE_DIAMOND_PRICE,
  GET_DIAMS_WITH_IMAGE,
  tokenOptions,
} from "../../backend/graphql";
import ColorLink from "../widgets/ColorLink";
import ColorSpread from "../widgets/ColorSpread";
import DiamondProfile from "../widgets/DiamondProfile";
import EquivalenceAlert from "../widgets/EquivalenceAlert";
import SectionPaper from "../widgets/SectionPaper";
import SectionTitle from "../widgets/SectionTitle";
import { SheetGraphPercentile } from "./PricingComputation";
import { pricingColorQuery } from "../../tools";

const DiamondPricing = () => {
  const { id } = useParams();
  const token = useAppSelector((state) => state.auth.token);
  const { loading, error, data } = useQuery(GET_DIAMS_WITH_IMAGE, {
    ...tokenOptions(token),
    variables: {
      take: 100,
      skip: 0,
      filter: 'iD == "' + id + '"',
    },
  });

  useDocumentTitle(stoneName(data?.diamonds?.items[0]), "Price analysis");

  const [pricingReport] = useMutation(
    ANALYZE_DIAMOND_PRICE,
    tokenOptions(token),
  );
  const [schemes, setSchemes] = useState<any>(null);

  useEffect(() => {
    setSchemes(null);
    pricingReport({
      variables: {
        id: id,
      },
    }).then((t) => {
      setSchemes(t?.data?.analyzeDiamondPrice);
    });
  }, [pricingReport, id]);

  if (loading || !schemes) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;
  if (!id) return <p>No diamond found!</p>;

  const foundDiamond = data?.diamonds?.items[0];

  // The header, then a single card holding every pricing scheme's valuation, gutter-spaced from the
  // header like the analysis screen — no outer wrapping Paper.
  return (
    <Stack spacing={1} useFlexGap>
      {foundDiamond && <DiamondProfile data={foundDiamond} page="price" />}
      <SectionPaper>
        <Stack spacing={3} useFlexGap>
          {schemes.schemes
            // Langerman colors are still too blurry to price a stone against
            .filter(
              (scheme: any) =>
                scheme.pricing.equivalentColors?.system !== "Langerman",
            )
            .map((scheme: any, index: number) => {
              // A scheme names its color through its equivalent colors, which every scheme priced
              // for a stone has: they are all queried on one of the stone's own colors.
              const schemeColor = pricingColorQuery(scheme.pricing);
              return (
                <Box key={index}>
                  <SectionTitle>
                    <ColorLink label={schemeColor} color={schemeColor} />
                    -based evaluation
                  </SectionTitle>
                  {scheme.isValid ? (
                    <>
                      <EquivalenceAlert
                        equivalentColors={scheme.pricing.equivalentColors}
                        sx={{ my: 1 }}
                      />
                      {scheme.suggestedPricingRange !==
                        scheme.normalizedPricingRange && (
                        <Typography>
                          Pricing factors: {scheme.pricingFactors}
                        </Typography>
                      )}
                      <Typography>
                        Suggested pricing:&nbsp;&nbsp;
                        {scheme.suggestedPricingRange}
                      </Typography>
                      {scheme.suggestedPricingRange !==
                        scheme.normalizedPricingRange && (
                        <Typography>
                          Normalized pricing:&nbsp;
                          {scheme.normalizedPricingRange}
                        </Typography>
                      )}
                      <Box sx={{ height: 500, marginTop: "5px" }}>
                        <AutoSizer>
                          {(size: Size) => (
                            <SheetGraphPercentile
                              refPoint={id}
                              show={[
                                { value: schemeColor, label: schemeColor },
                              ]}
                              isNormalized={false}
                              withSamples={true}
                              width={size.width}
                              height={500}
                              selectedClarity={foundDiamond.clarity}
                              data={{
                                prices: [scheme.pricing],
                                diams: scheme.diams,
                                clarities: schemes.clarities,
                                buckets: schemes.buckets,
                              }}
                            />
                          )}
                        </AutoSizer>
                      </Box>
                      {scheme.pricing.equivalentColors?.system !== "GIA" && (
                        <ColorSpread
                          hideIfSingleCategory={true}
                          allowedColorSystems={["GIA"]}
                          colorSpreads={scheme.colorSpreads}
                          style={{ marginTop: 3 }}
                        />
                      )}
                    </>
                  ) : (
                    <Typography>
                      Unfortunately we don't have enough data for this specific
                      case to provide a reasonable estimate.
                    </Typography>
                  )}
                </Box>
              );
            })}
        </Stack>
      </SectionPaper>
    </Stack>
  );
};

export default DiamondPricing;
